import { lazy } from "react";

const LAZY_LOAD_PATTERNS = [
  "chunkloaderror",
  "loading chunk",
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "unable to preload css",
  "importing a module script failed",
  "failed to load module script",
  "error loading chunk",
];

const RELOAD_WINDOW_MS = 10_000;
const RELOAD_AT_KEY = "lazy-reload-at";

/** True for failed route/chunk fetches; false for real render bugs. */
export function isLazyLoadError(error) {
  if (!error) return false;
  const name = String(error.name || "").toLowerCase();
  const message = String(error.message || "").toLowerCase();
  if (name === "chunkloaderror") return true;
  return LAZY_LOAD_PATTERNS.some((pattern) => message.includes(pattern));
}

function readSessionStore(storage) {
  if (storage) return storage;
  try {
    if (typeof sessionStorage === "undefined") return null;
    sessionStorage.getItem(RELOAD_AT_KEY);
    return sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Allow one auto-reload per short window so a stale tab after deploy recovers,
 * without locking the rest of the session behind a once-per-tab flag.
 * sessionStorage may throw in Safari private mode — treat as "allow reload".
 */
export function shouldAutoReloadLazyError(now = Date.now(), storage) {
  const store = readSessionStore(storage);
  if (!store) return true;
  try {
    const last = Number(store.getItem(RELOAD_AT_KEY) || 0);
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    store.setItem(RELOAD_AT_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

export function clearLazyReloadAttempt(storage) {
  const store = readSessionStore(storage);
  try {
    store?.removeItem(RELOAD_AT_KEY);
  } catch {
    /* ignore */
  }
}

/** Retry a dynamic import before surfacing the error boundary. */
export function lazyWithRetry(importer, retries = 2) {
  return lazy(() => {
    const attempt = (left) =>
      importer().catch((err) => {
        if (!isLazyLoadError(err) || left <= 0) throw err;
        return new Promise((resolve) => {
          setTimeout(() => resolve(attempt(left - 1)), 400);
        });
      });
    return attempt(retries);
  });
}
